import { captureRegion, rootStyleForRegion } from './screenshotRegion';
import {
  crossOriginUrlPattern,
  finishRunningAnimations,
  prepareClonedNode,
  shouldCaptureNode,
} from './screenshotFixes';

/** Oltre questo tempo la segnalazione parte senza screenshot. */
export const CAPTURE_TIMEOUT_MS = 8000;
/** Tempo massimo per scaricare una singola immagine o un carattere. */
const ASSET_TIMEOUT_MS = 5000;
/** Immagine trasparente 1x1 al posto di quelle che non si riescono a scaricare. */
const TRANSPARENT_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/** Risolve con il risultato della promessa, oppure null se fallisce o supera ms. */
export function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return new Promise<T | null>((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      },
    );
  });
}

/** Colore di sfondo della pagina (body, poi html, poi bianco) per riempire l'immagine. */
function pageBackground(): string {
  const transparent = (c: string) => !c || c === 'transparent' || c === 'rgba(0, 0, 0, 0)';
  const body = getComputedStyle(document.body).backgroundColor;
  if (!transparent(body)) return body;
  const html = getComputedStyle(document.documentElement).backgroundColor;
  return transparent(html) ? '#ffffff' : html;
}

async function capture(): Promise<Blob | null> {
  const { domToBlob } = await import('modern-screenshot');

  // Esattamente la finestra visibile, con lo scorrimento corrente (vedi screenshotRegion.ts).
  const region = captureRegion({
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    scrollX: window.scrollX,
    scrollY: window.scrollY,
    devicePixelRatio: window.devicePixelRatio,
  });

  finishRunningAnimations(document);

  return domToBlob(document.body, {
    width: region.width,
    height: region.height,
    scale: region.scale,
    type: 'image/png',
    backgroundColor: pageBackground(),
    style: rootStyleForRegion(region) as Partial<CSSStyleDeclaration>,
    timeout: ASSET_TIMEOUT_MS,
    // Pannelli interni scorsi (lista, dettaglio): si vede la parte scorsa, non l'inizio.
    features: { restoreScrollPosition: true },
    filter: shouldCaptureNode,
    onCloneEachNode: prepareClonedNode,
    fetch: {
      requestInit: { cache: 'force-cache' },
      bypassingCache: crossOriginUrlPattern(window.location.origin),
      placeholderImage: TRANSPARENT_PIXEL,
    },
  });
}

/**
 * Screenshot della parte visibile della pagina, con i dati sensibili offuscati nella copia
 * (il DOM vero non viene toccato). Restituisce null se la cattura fallisce o supera
 * CAPTURE_TIMEOUT_MS: chi chiama apre comunque il modulo di segnalazione, senza immagine.
 */
export async function captureScreenshot(): Promise<Blob | null> {
  try {
    return await withTimeout(capture(), CAPTURE_TIMEOUT_MS);
  } catch {
    return null;
  }
}
